"use client";

/**
 * IntegrationStatusDisplay - Integration connection status and management
 */

import React, { useState } from "react";
import { Tag, Alert } from "@/ui";
import { colors, spacing, radius, textStyles, primitives } from "@/ui/tokens";
import {
  Extension,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Sync,
  LinkOff,
  ExtensionOff,
} from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface Integration {
  id: string;
  name: string;
  type?: string;
  status: "connected" | "disconnected" | "error" | "syncing" | "warning";
  lastSync?: string;
  syncCount?: number;
  errorMessage?: string;
  icon?: string;
  metadata?: Record<string, unknown>;
}

interface IntegrationOutput {
  success?: boolean;
  integrations?: Integration[];
  integration?: Integration;
  message?: string;
  error?: string;
}

const statusConfig: Record<string, { icon: React.ReactNode; color: "success" | "danger" | "warning" | "info" | "neutral"; label: string }> = {
  connected: { icon: <CheckCircle />, color: "success", label: "Connected" },
  disconnected: { icon: <LinkOff />, color: "neutral", label: "Disconnected" },
  error: { icon: <ErrorIcon />, color: "danger", label: "Error" },
  syncing: { icon: <Sync />, color: "info", label: "Syncing" },
  warning: { icon: <Warning />, color: "warning", label: "Warning" },
};

function IntegrationCard({ integration }: { integration: Integration }) {
  const config = statusConfig[integration.status] || statusConfig.disconnected;

  return (
    <div
      style={{
        border: `1px solid ${colors.border.subtle}`,
        borderRadius: radius.md,
        marginBottom: spacing[2],
        backgroundColor: colors.canvas.default,
        padding: `${spacing[2]}px ${spacing[4]}px`,
        display: "flex",
        alignItems: "center",
        gap: spacing[4],
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          backgroundColor: primitives.neutral[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Extension style={{ fontSize: 20, color: colors.text.muted }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
          <span style={{ ...textStyles.body2, fontWeight: 600 }}>
            {integration.name}
          </span>
          <Tag
            icon={config.icon}
            color={config.color}
            size="sm"
          >
            {config.label}
          </Tag>
        </div>
        <div style={{ marginTop: spacing[1] }}>
          {integration.type && (
            <span style={{ ...textStyles.caption, color: colors.text.muted, marginRight: spacing[4] }}>
              Type: {integration.type}
            </span>
          )}
          {integration.lastSync && (
            <span style={{ ...textStyles.caption, color: colors.text.muted, marginRight: spacing[4] }}>
              Last sync: {new Date(integration.lastSync).toLocaleString()}
            </span>
          )}
          {integration.syncCount !== undefined && (
            <span style={{ ...textStyles.caption, color: colors.text.muted }}>
              {integration.syncCount} items synced
            </span>
          )}
          {integration.errorMessage && (
            <span style={{ ...textStyles.caption, color: colors.signal.danger, display: "block", marginTop: spacing[1] }}>
              {integration.errorMessage}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function IntegrationStatusDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as IntegrationOutput | undefined;
  const integrations = result?.integrations || (result?.integration ? [result.integration] : []);
  const isSuccess = result?.success !== false;

  // Group by status
  const connectedCount = integrations.filter((i) => i.status === "connected").length;
  const errorCount = integrations.filter((i) => i.status === "error").length;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title="Integration Status"
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Extension style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Checking integrations..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && integrations.length > 0 && (
          <div>
            {/* Summary */}
            <div
              style={{
                padding: `${spacing[3]}px ${spacing[5]}px`,
                backgroundColor: primitives.neutral[50],
                borderBottom: `1px solid ${colors.border.subtle}`,
                display: "flex",
                gap: spacing[4],
              }}
            >
              <Tag
                icon={<CheckCircle style={{ fontSize: 14 }} />}
                color="success"
                size="sm"
                variant="default"
              >
                {`${connectedCount} connected`}
              </Tag>
              {errorCount > 0 && (
                <Tag
                  icon={<ErrorIcon style={{ fontSize: 14 }} />}
                  color="danger"
                  size="sm"
                  variant="default"
                >
                  {`${errorCount} errors`}
                </Tag>
              )}
            </div>

            {/* Integration List */}
            <div style={{ padding: spacing[4] }}>
              {integrations.map((integration) => (
                <IntegrationCard key={integration.id} integration={integration} />
              ))}
            </div>
          </div>
        )}

        {/* Message */}
        {!isLoading && result?.message && (
          <div style={{ padding: spacing[4] }}>
            <Alert variant={isSuccess ? "success" : "error"} title={isSuccess ? "Success" : "Error"}>
              {result.message}
            </Alert>
          </div>
        )}

        {!isLoading && integrations.length === 0 && !result?.message && !isError && (
          <ToolEmptyState icon={<ExtensionOff />} message="No integrations found" />
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default IntegrationStatusDisplay;
